import { Identifiable, ITableRowProps, OnRowClick } from "layout/TableConstructor/types";
import { useTableConstructor } from "layout/TableConstructor/context/tableConstructorContext";
import { Dispatch, SetStateAction, useState } from "react";
import { IconButton } from "@mui/material";
import MuiTableRow from "@mui/material/TableRow";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import TableCell from "./TabelCell";

const CollapseButtonCell = ({ open, setOpen }: { open: boolean; setOpen: Dispatch<SetStateAction<boolean>> }) => (
  <TableCell onClick={(e) => e.stopPropagation()}>
    <IconButton
      sx={{ color: "base.200" }}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(!open);
      }}
      size='small'>
      {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
    </IconButton>
  </TableCell>
);

const CollapsingRow = <T extends Identifiable, CollapsingRowName extends keyof T | undefined = undefined>({
  isCollapsingRow,
  collapsingRowName,
  row,
  open,
  onRowClick,
}: {
  isCollapsingRow: boolean | undefined;
  row: T;
  collapsingRowName: CollapsingRowName | undefined;
  onRowClick: OnRowClick<T, CollapsingRowName> | undefined;
  open: boolean;
}) => {
  const { visibleColumnConfig } = useTableConstructor();

  if (
    isCollapsingRow &&
    collapsingRowName &&
    Array.isArray(row[collapsingRowName]) &&
    row[collapsingRowName]?.length &&
    open
  ) {
    const collapsingRow = row[collapsingRowName];
    return collapsingRow.map((collapsedRow) => (
      <MuiTableRow
        onClick={() => onRowClick?.(row, collapsedRow)}
        sx={{ backgroundColor: "#fff", cursor: onRowClick ? "pointer" : "default" }}
        key={`collapsed-row-${collapsedRow.id}`}>
        <TableCell />
        {visibleColumnConfig.map((col) => (
          <TableCell key={`cell-row-${collapsedRow.id}-col-${col.name}`}>
            {"renderCollapse" in col && typeof col.renderCollapse === "function"
              ? // @ts-expect-error порешать потом с типом TODO
                col.renderCollapse(collapsedRow)
              : col.render(row)}
          </TableCell>
        ))}
      </MuiTableRow>
    ));
  }

  return null;
};

const Row = <T extends Identifiable, CollapsingRowName extends keyof T | undefined = undefined>({
  row,
  collapsingRowName,
  checkIsCollapsingRow,
  onRowClick,
}: ITableRowProps<T, CollapsingRowName>) => {
  const { visibleColumnConfig } = useTableConstructor();

  const [open, setOpen] = useState(false);

  const isCollapsingRow = checkIsCollapsingRow?.(row);

  return (
    <>
      <MuiTableRow
        onClick={() => onRowClick?.(row)}
        sx={{
          backgroundColor: isCollapsingRow ? "#fff" : undefined,
          cursor: onRowClick ? "pointer" : "default",
          "&:last-of-type": {
            "& .MuiTableCell-root": {
              "&:first-of-type": {
                borderLeftStyle: "solid",
                borderBottomLeftRadius: 8,
              },
              "&:last-of-type": {
                borderBottomRightRadius: 8,
              },
            },
          },
        }}
        key={`row-${row.id}`}>
        {isCollapsingRow && (
          <CollapseButtonCell
            open={open}
            setOpen={setOpen}
          />
        )}
        {collapsingRowName && !isCollapsingRow && <TableCell />}
        {visibleColumnConfig.map((col) => (
          <TableCell
            key={`cell-row-${row.id}-col-${col.name}`}
            sx={col.cellStyle}>
            {col.render(row)}
          </TableCell>
        ))}
      </MuiTableRow>
      <CollapsingRow
        open={open}
        isCollapsingRow={isCollapsingRow}
        row={row}
        onRowClick={onRowClick}
        collapsingRowName={collapsingRowName}
      />
    </>
  );
};

export default Row;
